import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, Button, CircularProgress, Chip, Switch, TextField,
  ToggleButtonGroup, ToggleButton, Table, TableBody, TableCell, TableHead, TableRow, Alert,
} from '@mui/material';
import { Refresh, Save } from '@mui/icons-material';
import axios from 'axios';

// ☄️ Moon Plinko — статистика и настройки (slots-backend /api/admin/plinko).
const PLINKO_API = 'https://slots.moonraid.online/api/admin/plinko';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });

const f2 = (x) => (Number.isFinite(+x) ? (+x).toLocaleString('ru-RU', { maximumFractionDigits: 2 }) : '—');
const pct = (x) => (x == null ? '—' : `${(x * 100).toFixed(2)}%`);
const profitColor = (x) => (x > 0 ? '#4caf50' : x < 0 ? '#ef5350' : '#fff');
const RISK = { low: 'Низкий', medium: 'Средний', high: 'Высокий' };

const Stat = ({ label, value, color, hint }) => (
  <Card sx={{ bgcolor: '#0e1424', border: '1px solid rgba(255,255,255,0.06)', height: '100%' }}>
    <CardContent sx={{ py: 1.5 }}>
      <Typography variant="caption" sx={{ color: '#8fa1c4' }}>{label}</Typography>
      <Typography variant="h6" sx={{ color: color || '#fff', fontWeight: 800 }}>{value}</Typography>
      {hint && <Typography variant="caption" sx={{ color: '#5b6b8c' }}>{hint}</Typography>}
    </CardContent>
  </Card>
);

const Period = ({ title, s }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle2" sx={{ color: '#8fa1c4', mb: 1 }}>{title}</Typography>
    <Grid container spacing={1}>
      <Grid item xs={6} sm={3}><Stat label="Оборот" value={`${f2(s.turnover)}`} hint={`${s.balls} шариков, ${s.drops} бросков`} /></Grid>
      <Grid item xs={6} sm={3}><Stat label="Профит казино" value={f2(s.profit)} color={profitColor(s.profit)} /></Grid>
      <Grid item xs={6} sm={3}><Stat label="RTP факт" value={pct(s.rtp)} hint="цель 93%" /></Grid>
      <Grid item xs={6} sm={3}><Stat label="Игроков / лучший икс" value={`${s.players} / ×${f2(s.bestMult)}`} /></Grid>
    </Grid>
  </Box>
);

export default function PlinkoAdmin() {
  const [stats, setStats] = useState(null);
  const [players, setPlayers] = useState([]);
  const [drops, setDrops] = useState([]);
  const [period, setPeriod] = useState('today');
  const [sort, setSort] = useState('net');
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, d, st] = await Promise.all([
        axios.get(`${PLINKO_API}/stats`, { headers: authHeaders() }),
        axios.get(`${PLINKO_API}/drops`, { headers: authHeaders() }),
        axios.get(`${PLINKO_API}/settings`, { headers: authHeaders() }),
      ]);
      if (s.data?.success) setStats(s.data);
      if (d.data?.success) setDrops(d.data.drops);
      if (st.data?.success) setSettings((cur) => cur || st.data.settings);
    } catch (e) { /* тихо */ }
    setLoading(false);
  }, []);

  const loadPlayers = useCallback(async () => {
    try {
      const r = await axios.get(`${PLINKO_API}/players`, { params: { period, sort }, headers: authHeaders() });
      if (r.data?.success) setPlayers(r.data.players);
    } catch (e) { /* тихо */ }
  }, [period, sort]);

  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);
  useEffect(() => { loadPlayers(); }, [loadPlayers]);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await axios.post(`${PLINKO_API}/settings`, { settings }, { headers: authHeaders() });
      setMsg(r.data?.success ? { type: 'success', text: 'Сохранено, действует сразу' } : { type: 'error', text: r.data?.error || 'Ошибка' });
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Ошибка сохранения' });
    }
    setSaving(false);
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  const fe = stats?.features;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6">☄️ Moon Plinko</Typography>
        <Chip size="small" label="только /plinko в боте" variant="outlined" />
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<Refresh />} onClick={() => { load(); loadPlayers(); }}>Обновить</Button>
      </Box>

      {stats && (
        <>
          <Period title="Сегодня" s={stats.today} />
          <Period title="7 дней" s={stats.week} />
          <Period title="Всё время" s={stats.all} />

          <Typography variant="subtitle2" sx={{ color: '#8fa1c4', mb: 1 }}>Режимы (всё время): факт против теории</Typography>
          <Box sx={{ overflowX: 'auto', mb: 2 }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Ряды</TableCell><TableCell>Риск</TableCell><TableCell align="right">Шариков</TableCell>
                <TableCell align="right">Оборот</TableCell><TableCell align="right">Профит</TableCell>
                <TableCell align="right">RTP факт</TableCell><TableCell align="right">Теория</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {stats.modes.map((m) => (
                  <TableRow key={`${m.rows}-${m.risk}`}>
                    <TableCell>{m.rows}</TableCell><TableCell>{RISK[m.risk]}</TableCell>
                    <TableCell align="right">{m.balls}</TableCell><TableCell align="right">{f2(m.turnover)}</TableCell>
                    <TableCell align="right" sx={{ color: profitColor(m.profit), fontWeight: 700 }}>{f2(m.profit)}</TableCell>
                    <TableCell align="right">{pct(m.rtp)}</TableCell><TableCell align="right">{pct(m.theoryRtp)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {fe && fe.balls > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label={`Золотые: ${fe.gold} (${(fe.gold / fe.balls * 100).toFixed(2)}%)`} sx={{ bgcolor: 'rgba(255,203,77,0.15)' }} />
              <Chip label={`Космические: ${fe.cosmic} (${(fe.cosmic / fe.balls * 100).toFixed(2)}%)`} sx={{ bgcolor: 'rgba(255,90,216,0.15)' }} />
              <Chip label={`Треснули: ${fe.cracked}`} />
              <Chip label={`В метку: ${fe.marked} (1 из ${fe.marked ? Math.round(fe.balls / fe.marked) : '—'})`} sx={{ bgcolor: 'rgba(255,190,60,0.15)' }} />
              <Chip label={`Дождей: ${fe.rains} (${fe.rainBalls} бесплатных шариков)`} sx={{ bgcolor: 'rgba(120,205,255,0.15)' }} />
              <Chip label={`Искры: ${fe.sparks}`} sx={{ bgcolor: 'rgba(255,228,107,0.12)' }} />
              <Chip label={`Воронки: ${fe.vortex}`} sx={{ bgcolor: 'rgba(141,108,255,0.15)' }} />
              <Chip label={`Упёрлись в потолок: ${fe.capped}`} />
            </Box>
          )}
        </>
      )}

      <Typography variant="subtitle2" sx={{ color: '#8fa1c4', mb: 1 }}>Игроки</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <ToggleButtonGroup size="small" exclusive value={period} onChange={(e, v) => v && setPeriod(v)}>
          <ToggleButton value="today">Сегодня</ToggleButton><ToggleButton value="7d">7 дней</ToggleButton>
          <ToggleButton value="30d">30 дней</ToggleButton><ToggleButton value="all">Всё</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup size="small" exclusive value={sort} onChange={(e, v) => v && setSort(v)}>
          <ToggleButton value="net">Выиграли</ToggleButton><ToggleButton value="bet">Оборот</ToggleButton><ToggleButton value="rtp">RTP</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box sx={{ overflowX: 'auto', mb: 3 }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Игрок</TableCell><TableCell align="right">Оборот</TableCell><TableCell align="right">Итог игрока</TableCell>
            <TableCell align="right">RTP</TableCell><TableCell align="right">Шариков</TableCell><TableCell align="right">Лучший икс</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {players.length === 0 && <TableRow><TableCell colSpan={6} sx={{ color: '#5b6b8c' }}>Пока пусто</TableCell></TableRow>}
            {players.map((p) => (
              <TableRow key={p.userId}>
                <TableCell>{p.name} <Typography component="span" variant="caption" sx={{ color: '#5b6b8c' }}>{p.telegramId}</Typography></TableCell>
                <TableCell align="right">{f2(p.turnover)}</TableCell>
                <TableCell align="right" sx={{ color: profitColor(p.net), fontWeight: 700 }}>{f2(p.net)}</TableCell>
                <TableCell align="right">{pct(p.rtp)}</TableCell>
                <TableCell align="right">{p.balls}</TableCell>
                <TableCell align="right">×{f2(p.best)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Typography variant="subtitle2" sx={{ color: '#8fa1c4', mb: 1 }}>Последние броски</Typography>
      <Box sx={{ overflowX: 'auto', mb: 3, maxHeight: 360 }}>
        <Table size="small" stickyHeader>
          <TableHead><TableRow>
            <TableCell>Когда</TableCell><TableCell>Игрок</TableCell><TableCell>Режим</TableCell>
            <TableCell align="right">Ставка × шт</TableCell><TableCell align="right">Выплата</TableCell><TableCell align="right">Лучший</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {drops.map((d) => (
              <TableRow key={d.dropId}>
                <TableCell>{new Date(d.at).toLocaleTimeString('ru-RU', { timeZone: 'Europe/Moscow' })}</TableCell>
                <TableCell>{d.name}</TableCell>
                <TableCell>{d.rows} / {RISK[d.risk]}</TableCell>
                <TableCell align="right">{f2(d.bet)} × {d.balls}</TableCell>
                <TableCell align="right" sx={{ color: profitColor(d.paid - d.turnover) }}>{f2(d.paid)}</TableCell>
                <TableCell align="right">×{f2(d.best)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {settings && (
        <Card sx={{ bgcolor: '#0e1424', border: '1px solid rgba(255,255,255,0.06)', maxWidth: 560 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Настройки</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Switch checked={!!settings.plinko_enabled} onChange={(e) => setSettings({ ...settings, plinko_enabled: e.target.checked })} />
              <Typography>{settings.plinko_enabled ? 'Игра включена' : 'Игра выключена'}</Typography>
            </Box>
            <Grid container spacing={1.5}>
              {[
                ['plinko_min_bet', 'Мин ставка за шарик', 0.1],
                ['plinko_max_bet', 'Макс ставка за шарик', 0.1],
                ['plinko_payout_cap', 'Макс выплата за шарик', 1],
                ['plinko_max_balls', 'Шариков в серии (макс)', 1],
              ].map(([k, label, step]) => (
                <Grid item xs={6} key={k}>
                  <TextField fullWidth size="small" type="number" label={label} inputProps={{ step }}
                    value={settings[k] ?? ''} onChange={(e) => setSettings({ ...settings, [k]: e.target.value === '' ? '' : Number(e.target.value) })} />
                </Grid>
              ))}
            </Grid>
            <Typography variant="caption" sx={{ display: 'block', color: '#5b6b8c', mt: 1 }}>
              RTP 93% во всех режимах с учётом событий: лунные метки, золотой/космический (могут треснуть), искра,
              воронка, метеоритный дождь (копилка: 2.5/5/7% ставки по риску; при объёме 80 ставок — 5 метеоритов ×3 с метками ×5/×10, в среднем отдаёт копилку), потолок ×500 за шарик. Таблицы и шансы зашиты
              в математику (slots/backend/src/services/plinko/plinkoMath.js) и отсюда не меняются. Оборот и RTP — без
              бесплатных шариков дождя (их выплата входит в «выплачено»).
            </Typography>
            {msg && <Alert severity={msg.type} sx={{ mt: 1.5 }}>{msg.text}</Alert>}
            <Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving} sx={{ mt: 1.5 }}>Сохранить</Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
